import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Button,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
} from '@mui/material';

import UploadFileIcon from '@mui/icons-material/UploadFile';
import CreditCardIcon from '@mui/icons-material/CreditCard';

import TopNav from './Navbar';
import LeftNavbar from './LeftNav';
import Breadcrumb from './BreadCrump';

import { useUser } from './useUserHook';

// Card ID generator
const generateCardId = (username = '') => {
  if (!username || username.length < 1) return 'INVALID-ID';

  const firstLetter = username[0];
  const lastLetter = username[username.length - 1];

  const now = new Date();
  const datePart = `${now.getFullYear()}${(now.getMonth() + 1)
    .toString()
    .padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}`;
  const timePart = `${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}${now
    .getSeconds()
    .toString()
    .padStart(2, '0')}${now.getMilliseconds().toString().padStart(3, '0')}`;

  const randomPart = Math.random().toString(36).substring(2, 8).toUpperCase();

  return `ID-${firstLetter}${lastLetter}-${datePart}-${timePart}-${randomPart}`;
};

const ICard = () => {
  const { user, error } = useUser();
  const [selectedCourse, setSelectedCourse] = useState('');
  const [profileImage, setProfileImage] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  const [userData, setUserData] = useState({
    username: '',
    email: '',
    course: '',
    cardId: '',
  });

  const [previousCards, setPreviousCards] = useState([]);

  // Load user data and generate card ID
  useEffect(() => {
    if (user) {
      const generatedId = generateCardId(user.username);
      const defaultCourse = user.enrolledCourses?.[0]?.title || '';

      setSelectedCourse(defaultCourse);
      setUserData({
        username: user.username,
        email: user.email,
        course: defaultCourse,
        cardId: generatedId,
      });
    }
  }, [user]);

  // Fetch previous iCards
  useEffect(() => {
    const fetchICards = async () => {
      try {
        const res = await fetch('http://196.216.9.34:5020/api/icards/get-cards', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (!res.ok) throw new Error('Failed to fetch iCards');
        const data = await res.json();
        setPreviousCards(data);
      } catch (error) {
        console.error('Failed to fetch previous iCards:', error);
      }
    };

    fetchICards();
  }, []);

  // Handle course selection
  const handleCourseChange = (e) => {
    const course = e.target.value;
    setSelectedCourse(course);
    setUserData((prev) => ({ ...prev, course }));
  };

  // File preview
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const previewURL = URL.createObjectURL(file);
    setProfileImage(previewURL);
    setSelectedFile(file);
  };

  // Submit ID card request
  const handleSubmit = async () => {
    if (!selectedFile) {
      alert('Please upload a picture first');
      return;
    }

    const formData = new FormData();
    formData.append('profileImage', selectedFile);
    formData.append('username', userData.username);
    formData.append('email', userData.email);
    formData.append('course', selectedCourse);
    formData.append('card_id', userData.cardId);

    try {
      const res = await fetch('http://196.216.9.34:5020/api/icards/create-card', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      if (!res.ok) throw new Error('Failed to create iCard');

      const updated = await fetch('http://196.216.9.34:5020/api/icards/get-cards', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const data = await updated.json();
      setPreviousCards(data);

      setSelectedFile(null);
      setProfileImage('');
    } catch (err) {
      console.error('Image upload or iCard creation failed:', err);
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <LeftNavbar />
      <Box sx={{ flexGrow: 1, ml: { lg: '240px' } }}>
        <TopNav />
        <Box sx={{ p: 10, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
          <Breadcrumb currentPage="Exam ID Card" />

          <Typography variant="h4" fontWeight="bold" sx={{ color: '#ff6600', mb: 4 }}>
            <CreditCardIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Exam ID Card Generator
          </Typography>

          <Grid container spacing={4}>
            {/* Upload + Submit Section */}
            <Grid item xs={12} md={4}>
              <Card sx={{ textAlign: 'center', p: 3 }}>
                <CardContent>
                  <Avatar
                    src={profileImage}
                    alt="User"
                    sx={{ width: 100, height: 100, margin: '0 auto', mb: 2, bgcolor: '#ff6600' }}
                  />

                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadFileIcon />}
                    sx={{ borderColor: '#ff6600', color: '#ff6600' }}
                  >
                    Upload Picture
                    <input type="file" accept="image/*" hidden onChange={handleFileChange} />
                  </Button>

                  {/* Course Dropdown */}
                  {user?.enrolledCourses?.length > 0 ? (
                    <Select
                      value={selectedCourse}
                      onChange={handleCourseChange}
                      displayEmpty
                      fullWidth
                      sx={{ mt: 3 }}
                    >
                      {user.enrolledCourses.map((course) => (
                        <MenuItem key={course.id} value={course.title}>
                          {course.title}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      You are not enrolled in any courses.
                    </Typography>
                  )}

                  {/* Submit Button */}
                  <Button
                    variant="contained"
                    sx={{ mt: 3, bgcolor: '#ff6600', color: '#fff', width: '100%' }}
                    onClick={handleSubmit}
                    disabled={!selectedFile || !selectedCourse}
                  >
                    Submit
                  </Button>

                  <Divider sx={{ my: 3 }} />
                  <Typography variant="body2" color="text.secondary">
                    Please upload a clear headshot photo of yourself to generate your ID card.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Preview ID Card */}
            <Grid item xs={12} md={8} alignItems="center">
  <Card
    sx={{
      p: 4,
      borderRadius: 4,
      bgcolor: 'linear-gradient(135deg, #fffaf0 0%, #fff0e6 100%)',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      overflow: 'hidden',
      position: 'relative',
    }}
  >
    {/* Decorative top bar */}
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '8px',
        bgcolor: '#ff6600',
      }}
    />

    <CardContent>
      <Typography
        variant="h5"
        fontWeight="bold"
        textAlign="center"
        sx={{
          color: '#ff6600',
          mb: 4,
          letterSpacing: 1,
          textTransform: 'uppercase',
        }}
      >
        🎓 Official Exam ID Card
      </Typography>

      <Grid container spacing={4} alignItems="center">
        {/* Profile image with glow */}
        <Grid item xs={12} sm={4}>
          <Avatar
            src={profileImage}
            sx={{
              width: 110,
              height: 110,
              mx: 'auto',
              border: '3px solid #ff6600',
              boxShadow: '0 4px 12px rgba(255, 102, 0, 0.4)',
            }}
          />
        </Grid>

        {/* Details with highlighted labels */}
        <Grid item xs={12} sm={8}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" color="#ff6600">
              Username
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {userData.username}
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" color="#ff6600">
              Course
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {selectedCourse}
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" fontWeight="bold" color="#ff6600">
              Card ID
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {userData.cardId}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </CardContent>
  </Card>
</Grid>

          </Grid>

          {/* Previous Cards Table */}
          {previousCards.length > 0 && (
            <Box sx={{ mt: 6 }}>
              <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, color: '#ff6600' }}>
                🗂️ Previous Exam ID Cards
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead sx={{ bgcolor: '#ffe0cc' }}>
                    <TableRow>
                      <TableCell>Photo</TableCell>
                      <TableCell>Username</TableCell>
                     
                      <TableCell>Course</TableCell>
                      <TableCell>Card ID</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previousCards.map((card, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Avatar src={`http://196.216.9.34:5020${card.profile_image_url}`} sx={{ width: 40, height: 40 }} />
                        </TableCell>
                        <TableCell>{card.username}</TableCell>
                        
                        <TableCell>{card.course}</TableCell>
                        <TableCell>{card.card_id}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ICard;
